var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import clsx from 'clsx';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import { rem } from '../utils/Polished';
import Typography from '../Typography';
import SvgClose from '../Icon/SvgClose';
import SvgNotificationFilled from '../Icon/SvgNotificationFilled';
const floatingBannerStartingPoint = rem('-60px');
const floatingBannerEnteredPoint = (p) => p.top || p.theme.SizePositionNotificationBannerFloatingBase;
const floatingBannerWidthMobile = (p) => `calc(100% - ${p.theme.SizeMarginNotificationBannerMobile})`;
const NotificationBannerSC = styled.div `
  outline: 0;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  height: auto;
  padding: ${(p) => p.theme.SizePaddingNotificationBannerBase};
  border-radius: ${(p) => p.theme.SizeBorderRadiusNotificationBannerBase};
  font-family: ${(p) => p.theme.FontFamilyBase};
  font-weight: ${(p) => p.theme.ContentFontWeightNormal};

  width: 100%;
  background-color: ${(p) => p.theme.ColorBackgroundNotificationBannerMedium};

  &._ndsNotificationBanner--has-onclick {
    ._ndsNotificationBanner__content {
      cursor: pointer;
    }
  }

  ._ndsNotificationBanner__title,
  ._ndsNotificationBanner__text {
    color: ${(p) => p.theme.ColorFontNotificationBannerMedium};
  }

  ._ndsNotificationBanner__icon {
    min-width: ${(p) => p.theme.SizeIconNotificationBannerBase};
    margin-right: ${(p) => p.theme.SizeMarginNotificationBannerIcon};
    fill: ${(p) => p.theme.ColorIconNotificationBannerMedium};
  }

  ._ndsNotificationBanner__close {
    width: ${(p) => p.theme.SizeIconNotificationBannerBase};
    min-width: ${(p) => p.theme.SizeIconNotificationBannerBase};
    margin-left: ${(p) => p.theme.SizeMarginNotificationBannerIcon};
    fill: ${(p) => p.theme.ColorIconNotificationBannerMedium};
    cursor: pointer;
  }

  &._ndsNotificationBanner--high {
    background-color: ${(p) => p.theme.ColorBackgroundNotificationBannerHigh};

    ._ndsNotificationBanner__title,
    ._ndsNotificationBanner__text {
      color: ${(p) => p.theme.ColorFontNotificationBannerHigh};
    }

    ._ndsNotificationBanner__icon,
    ._ndsNotificationBanner__close {
      fill: ${(p) => p.theme.ColorIconNotificationBannerHigh};
    }
  }

  &._ndsNotificationBanner--error {
    background-color: ${(p) => p.theme.ColorBackgroundNotificationBannerError};
  }

  &._ndsNotificationBanner--floating {
    position: fixed;
    width: ${floatingBannerWidthMobile};
    z-index: ${(p) => p.theme.zIndex.topLayer};
    top: ${floatingBannerEnteredPoint};
    left: ${(p) => p.theme.SizePositionNotificationBannerFloatingBase};
    right: ${(p) => p.theme.SizePositionNotificationBannerFloatingBase};
  }

  &._ndsNotificationBanner--transition {
    &-enter,
    &-appear {
      top: ${floatingBannerStartingPoint};
      opacity: 0;
    }
    &-enter-active,
    &-appear-active {
      top: ${floatingBannerEnteredPoint};
      opacity: 1;
      transition: top 300ms ease-in-out, opacity 300ms ease-in-out;
    }
    &-enter-done,
    &-appear-done {
      top: ${floatingBannerEnteredPoint};
      opacity: 1;
    }

    &-exit {
      top: ${floatingBannerEnteredPoint};
      opacity: 1;
    }
    &-exit-active {
      top: ${floatingBannerEnteredPoint};
      opacity: 0;
      transition: opacity 300ms ease-out;
    }
    &-exit-done {
      top: ${floatingBannerStartingPoint};
      opacity: 0;
    }
  }

  @media (min-width: ${(p) => p.theme.SizeBreakpointsM}) {
    &._ndsNotificationBanner--floating {
      width: ${(p) => p.theme.SizeWidthNotificationBannerFloatingDesktop};
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;
const IconAndLabelWrapper = styled.div `
  display: inline-flex;
  align-items: center;
`;
const TitleAndTextWrapper = styled.div `
  display: flex;
  flex-direction: column;
`;
const NotificationBannerInner = (props) => {
    const { attentionLevel, title, text, showCloseButton, handleClose, floating, top: topProp, error, icon, className: classNameProp, forwardedRef, onContentClick } = props, others = __rest(props, ["attentionLevel", "title", "text", "showCloseButton", "handleClose", "floating", "top", "error", "icon", "className", "forwardedRef", "onContentClick"]);
    const isHighAttention = React.useMemo(() => attentionLevel === 'high' || error || floating, [attentionLevel, error, floating]);
    const hasOnClick = React.useMemo(() => typeof onContentClick === 'function', [
        onContentClick
    ]);
    const classes = React.useMemo(() => clsx({
        '_ndsNotificationBanner--high': isHighAttention,
        '_ndsNotificationBanner--error': error,
        '_ndsNotificationBanner--floating': floating,
        '_ndsNotificationBanner--has-onclick': hasOnClick
    }, classNameProp), [classNameProp, isHighAttention, error, floating, hasOnClick]);
    const iconComp = React.useMemo(() => {
        if (icon && React.isValidElement(icon)) {
            return React.cloneElement(icon, {
                className: clsx('_ndsNotificationBanner__icon', icon.props && icon.props.className),
                'data-testid': 'NotificationBannerIcon'
            });
        }
        return (React.createElement(SvgNotificationFilled, { className: "_ndsNotificationBanner__icon", "data-testid": "NotificationBannerIcon" }));
    }, [icon]);
    const handleBannerClick = React.useCallback((e) => {
        if (hasOnClick) {
            onContentClick(e);
            handleClose(e);
        }
    }, [onContentClick, hasOnClick, handleClose]);
    return (React.createElement(NotificationBannerSC, Object.assign({ ref: forwardedRef, className: classes }, (floating && { top: topProp }), others),
        React.createElement(IconAndLabelWrapper, { onClick: handleBannerClick, className: "_ndsNotificationBanner__content" },
            iconComp,
            React.createElement(TitleAndTextWrapper, null,
                React.createElement(Typography, { className: "_ndsNotificationBanner__title", variant: "subhead1", "data-testid": "NotificaitonBannerTitle" }, title),
                text && (React.createElement(Typography, { className: "_ndsNotificationBanner__text", variant: "body4", "data-testid": "NotificaitonBannerText" }, text)))),
        showCloseButton && (React.createElement(SvgClose, { className: "_ndsNotificationBanner__close", "data-testid": "NotificationBannerClose", onClick: handleClose }))));
};
const NotificationBanner = (props) => {
    const { showCloseButton, onDismiss, floating } = props;
    const [show, setShow] = React.useState(true);
    const [dismissTimer, setDismissTimer] = React.useState(null);
    const handleClose = React.useCallback(() => {
        setShow(false);
        if (onDismiss) {
            onDismiss();
        }
    }, [onDismiss]);
    const startDismissTimer = React.useCallback(() => {
        clearTimeout(dismissTimer);
        const timer = setTimeout(() => handleClose(), 6000);
        setDismissTimer(timer);
    }, [handleClose, dismissTimer]);
    const onEntered = React.useCallback(() => {
        if (floating && !showCloseButton) {
            startDismissTimer();
        }
    }, [floating, showCloseButton, startDismissTimer]);
    return (React.createElement(CSSTransition, { in: show, timeout: 300, onEntered: onEntered, appear: true, unmountOnExit: true, classNames: "_ndsNotificationBanner--transition" },
        React.createElement(NotificationBannerInner, Object.assign({ handleClose: handleClose }, props))));
};
NotificationBanner.propTypes = {
    attentionLevel: PropTypes.oneOf(['high', 'medium']),
    title: PropTypes.string.isRequired,
    text: PropTypes.string,
    showCloseButton: PropTypes.bool,
    floating: PropTypes.bool,
    top: PropTypes.string,
    error: PropTypes.bool,
    icon: PropTypes.element,
    onContentClick: PropTypes.func,
    onDismiss: PropTypes.func,
    className: PropTypes.string,
    forwardedRef: PropTypes.any
};
NotificationBanner.defaultProps = {
    attentionLevel: 'medium',
    text: undefined,
    showCloseButton: undefined,
    floating: undefined,
    top: undefined,
    error: undefined,
    icon: undefined,
    onContentClick: null,
    onDismiss: null,
    className: undefined,
    forwardedRef: null
};
export { NotificationBanner as NotificationBannerRootComp };
export default withForwardedRef(NotificationBanner);
//# sourceMappingURL=NotificationBanner.js.map